import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Auth, Hub } from 'aws-amplify';
import { apiService } from '../services/apiService';

interface User {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'assessor' | 'viewer';
  tenantId: string;
  industryProfile: string;
  attributes?: {
    given_name?: string;
    family_name?: string;
    email?: string;
    'custom:role'?: string;
    'custom:tenantId'?: string;
    'custom:industryProfile'?: string;
  };
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  isAssessor: boolean;
  isViewer: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

// Shallow compare so we only emit a new user reference when something
// actually changed. Without this, every auth check creates a new object
// literal, triggering all downstream effects that depend on `user`.
const usersEqual = (a: User | null, b: User | null): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.username === b.username &&
    a.email === b.email &&
    a.firstName === b.firstName &&
    a.lastName === b.lastName &&
    a.role === b.role &&
    a.tenantId === b.tenantId &&
    a.industryProfile === b.industryProfile
  );
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // De-duplicates concurrent checkAuthState invocations so overlapping
  // Hub events don't fan out into multiple Cognito API calls.
  const checkInFlightRef = useRef(false);
  // Run the role self-heal at most once per session.
  const roleSyncedRef = useRef(false);

  const checkAuthState = async (bypassCache = false) => {
    if (checkInFlightRef.current) return;
    checkInFlightRef.current = true;

    try {
      let session;
      try {
        session = await Auth.currentSession();
      } catch {
        setUser((prev) => (prev === null ? prev : null));
        setLoading(false);
        return;
      }

      const tokenPayload: any = session.getIdToken().payload || {};

      let currentUser: any;
      let userAttributes: any = {};
      try {
        currentUser = bypassCache
          ? await Auth.currentAuthenticatedUser({ bypassCache: true })
          : await Auth.currentAuthenticatedUser();
        userAttributes = currentUser?.attributes || {};
      } catch (err) {
        console.log('Could not get current user', err);
        setUser((prev) => (prev === null ? prev : null));
        setLoading(false);
        return;
      }

      if (!currentUser || !currentUser.username) {
        setUser((prev) => (prev === null ? prev : null));
        setLoading(false);
        return;
      }

      const attributes = { ...userAttributes, ...tokenPayload };

      const firstName =
        attributes.given_name ||
        attributes['name.givenName'] ||
        currentUser.attributes?.given_name ||
        currentUser.attributes?.['name.givenName'] ||
        '';
      const lastName =
        attributes.family_name ||
        attributes['name.familyName'] ||
        currentUser.attributes?.family_name ||
        currentUser.attributes?.['name.familyName'] ||
        '';

      const role = attributes['custom:role'] || currentUser.attributes?.['custom:role'] || 'viewer';
      const tenantId =
        attributes['custom:tenantId'] || currentUser.attributes?.['custom:tenantId'] || 'default-tenant';
      const industryProfile =
        attributes['custom:industryProfile'] || currentUser.attributes?.['custom:industryProfile'] || 'GEN';

      const userData: User = {
        username: currentUser.username,
        email: attributes.email || currentUser.attributes?.email || tokenPayload.email || '',
        firstName,
        lastName,
        role: role as 'admin' | 'assessor' | 'viewer',
        tenantId,
        industryProfile,
      };

      setUser((prev) => (usersEqual(prev, userData) ? prev : userData));

      // Self-heal: if the ID token is missing custom:role OR custom:tenantId,
      // post-confirmation never fully provisioned this user. Call the backend
      // to create/fix the tenant + user records, then force a token refresh so
      // the new claims appear in subsequent API calls.
      const tokenHadRole = Boolean(tokenPayload['custom:role']);
      const tokenHadTenant = Boolean(tokenPayload['custom:tenantId']);
      if ((!tokenHadRole || !tokenHadTenant) && !roleSyncedRef.current) {
        roleSyncedRef.current = true;
        try {
          await apiService.syncMyRole();
          // Force Cognito to mint a new ID token with the updated custom:role.
          try {
            const sess = await Auth.currentSession();
            await new Promise<void>((resolve, reject) => {
              currentUser.refreshSession(sess.getRefreshToken(), (err: any) => {
                if (err) reject(err); else resolve();
              });
            });
          } catch (refreshErr) {
            console.warn('Token refresh after syncMyRole failed', refreshErr);
          }
          checkInFlightRef.current = false;
          return checkAuthState(true);
        } catch (e) {
          console.warn('syncMyRole failed', e);
        }
      }
    } catch (error) {
      console.log('No authenticated user', error);
      setUser((prev) => (prev === null ? prev : null));
    } finally {
      setLoading(false);
      checkInFlightRef.current = false;
    }
  };

  useEffect(() => {
    checkAuthState();

    const hubListener = Hub.listen('auth', (data: any) => {
      const { payload } = data;
      switch (payload.event) {
        case 'signIn':
          // Token already contains user attributes; no need to bypass cache.
          checkAuthState(false);
          break;
        case 'signUp':
          // Post Confirmation Lambda may have added custom attributes
          // that aren't in the cached user yet — force a fresh fetch.
          setTimeout(() => checkAuthState(true), 2000);
          break;
        case 'signOut':
          setUser(null);
          setLoading(false);
          break;
        // Do NOT handle 'tokenRefresh': Amplify manages token rotation
        // internally and re-running checkAuthState on every refresh was
        // the source of the infinite Cognito API loop.
        default:
          break;
      }
    });

    return () => {
      Hub.remove('auth', hubListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAdmin = user?.role === 'admin';
  // Assessor can create/manage assessments but cannot manage users
  // Admin also has assessor permissions (can create/manage assessments)
  const isAssessor = user?.role === 'assessor';
  // Viewer can only view assessments, cannot create/manage assessments or users
  // Admin and Assessor also have viewer permissions (can view assessments)
  const isViewer = user?.role === 'viewer' || user?.role === 'assessor' || user?.role === 'admin';

  const value: AuthContextType = {
    user,
    loading,
    isAdmin,
    isAssessor,
    isViewer,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
